// `search.page.tpl.html:890` — keyword conditions: which part of the resume, how to combine,
// and the terms.
//
// ⚠️ THIS SECTION ONLY EXISTS INSIDE THE SHORTLIST FRAME. `ng-if="state == SHORTLIST"`, which
// is the opposite of the title bar's condition — see the shell's section table. A standalone
// criteria builder has no keyword search at all.
//
// Two things it shares with the skill-set section and one it does not:
//
//   - the operator between rows is hardcoded to index 0, always "AND", with no radio group;
//   - the bottom row is two placeholders and an add button;
//   - but its tags-input has NO `add-from-autocomplete-only` and no autocomplete at all.
//     Keywords are free text by design — that is the point of a keyword search — so Enter adds
//     what was typed, which is the opposite of every other tag field in the builder.
import { keywordsCriteriaValid } from './criteriaValidity.ts'
import { useMemo, type ReactElement } from 'react'
import { TagsInput, type Tag } from '../common/TagsInput.tsx'
import { tagsPlaceholder } from './SkillSetSection.tsx'
import {
  KEYWORD_CONDITION_OPTIONS,
  KEYWORD_SECTION_OPTIONS,
  LOGICAL_OPERATOR_CLAUSE,
  LogicalOperator,
  type CriteriaConditionType,
  type KeywordSectionCategory,
} from './criteriaOptions.ts'

/** `ITempExtensionKeywordCriteria['conditions'][number]`. */
export interface KeywordCondition {
  sectionCategory?: KeywordSectionCategory
  conditionType?: CriteriaConditionType
  terms?: Tag[]
}

export interface KeywordSearchSectionProps {
  conditions: readonly KeywordCondition[]
  /**
   * The Field select's options. Defaults to the CANDIDATE list; the contacts builder has its
   * own two-entry list with different ordinals — see
   * `CLIENT_CONTACT_KEYWORD_SECTION_OPTIONS`, and the note there for what rendering the wrong
   * one costs on the wire.
   */
  sectionOptions?: ReadonlyArray<{ display: string; value: number }>
  onChange?: (index: number, condition: KeywordCondition) => void
  onRemove?: (index: number) => void
  onAdd?: () => void
}

export function KeywordSearchSection({
  conditions,
  sectionOptions,
  onChange,
  onRemove,
  onAdd,
}: KeywordSearchSectionProps): ReactElement {
  // ⚠️ VALIDITY IS EVENT-DRIVEN UPSTREAM, NOT CONTINUOUS, and computing it on every render
  // made this label appear where production shows none. `$setValidity` is called from
  // `addKeywordCriteriaCondition()` and `onKeywordChanged()` (tag added/removed) — never from the select's `ng-change` — so changing a row's dropdown
  // leaves the previous verdict standing. Keying the memo to the tag counts and the row count
  // reproduces that: a select change does not move the signature, so it does not revalidate.
  const validitySignature = conditions.map((condition) => condition.terms?.length ?? 0).join(',')
  // eslint-disable-next-line react-hooks/exhaustive-deps -- the signature IS the dependency; see above.
  const sectionValid = useMemo(() => keywordsCriteriaValid(conditions), [validitySignature])
  return (
    <>
      <div className="criteria-row">
        <div className="criteria-cell-label">
          <label className="bold-soft">Field</label>
        </div>
        <div className="criteria-cell-label">
          <label className="bold-soft">Condition</label>
        </div>
        <div className="criteria-cell-label">
          <label className="bold-soft">Keywords</label>
        </div>
      </div>

      {conditions.map((condition, index) => (
        // eslint-disable-next-line react/no-array-index-key -- positional upstream too.
        <div className="keyword-criteria sub-section criteria-row" key={index}>
          <div className="keyword-field criteria-cell">
            <div className="select">
              {/* `form-control` WITHOUT `input` — the only select in the builder missing it. */}
              <select
                className="form-control"
                value={condition.sectionCategory ?? ''}
                onChange={(event) =>
                  onChange?.(index, {
                    ...condition,
                    sectionCategory: Number(event.target.value) as KeywordSectionCategory,
                  })
                }
              >
                {(sectionOptions ?? KEYWORD_SECTION_OPTIONS).map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.display}
                  </option>
                ))}
              </select>
              {/* ⚠️ INSIDE the `.select` div, unlike the skills section where the operator is a
                  sibling of the input's wrapper. Moving it out changes what it is indented
                  against. */}
              {(condition.terms?.length ?? 0) > 0 && index < conditions.length - 1 ? (
                <div className="mT10">
                  <label className="bold-soft logical-operator-clauses">
                    {LOGICAL_OPERATOR_CLAUSE[LogicalOperator.All]}
                  </label>
                </div>
              ) : null}
            </div>
          </div>

          <div className="keyword-condition criteria-cell">
            <div className="select">
              <select
                className="form-control input"
                value={condition.conditionType ?? ''}
                onChange={(event) =>
                  onChange?.(index, {
                    ...condition,
                    conditionType: Number(event.target.value) as CriteriaConditionType,
                  })
                }
              >
                {KEYWORD_CONDITION_OPTIONS.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.display}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div className="keyword-input criteria-cell">
            <div>
              <TagsInput
                className="tags-input-default-width"
                name={`keywords${String(index)}`}
                placeholder={tagsPlaceholder(condition.terms, 'keyword')}
                tags={condition.terms ?? []}
                // Free text: a keyword is whatever the user types, so the typed value is the
                // suggestion. See the note at the top.
                suggestions={[]}
                freeText
                onAdd={(tag) => onChange?.(index, { ...condition, terms: [...(condition.terms ?? []), tag] })}
                onRemove={(at) =>
                  onChange?.(index, { ...condition, terms: (condition.terms ?? []).filter((_, i) => i !== at) })
                }
              />
            </div>
          </div>
          {/* `search.page.tpl.html:937` — a condition with no terms, once the section is
              invalid. Upstream keeps the label OUTSIDE the input's cell, as a sibling of the
              row's columns. */}
          {(condition.terms?.length ?? 0) === 0 && !sectionValid ? (
            <label className="control-label has-error">Please fill in at least one keyword</label>
          ) : null}

          <div
            className="remove-criteria-container criteria-cell"
            role="button"
            aria-label="Remove"
            tabIndex={0}
            onClick={() => onRemove?.(index)}
            onKeyDown={(event) => {
              if (event.key !== 'Enter' && event.key !== ' ') return
              event.preventDefault()
              onRemove?.(index)
            }}
          >
            <div className="fa-stack fa-1x icons-container">
              <i className="fas fa-circle fa-stack-1x x-icon-container fa-lg" />
              <i className="fal fa-times fa-stack-1x cursor-pointer fa-xs" />
            </div>
          </div>
        </div>
      ))}

      <div className="criteria-row bottom-cta-row sub-section">
        <div className="criteria-cell placeholder-cell" />
        <div className="criteria-cell placeholder-cell" />
        <div className="criteria-cell keywords-add-btn">
          <a
            href=""
            className="add-button"
            onClick={(event) => {
              event.preventDefault()
              onAdd?.()
            }}
          >
            <i className="fal fa-plus-circle" />
            <span>&nbsp;Add</span>
          </a>
        </div>
      </div>
    </>
  )
}
